import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Add16Regular,
  CallEnd20Filled,
  CallForward20Regular,
  CallInbound16Regular,
  CallMissed16Regular,
  CallOutbound16Regular,
  Call20Filled,
  Call20Regular,
  Chat20Regular,
  ChevronDown12Regular,
  Delete20Regular,
  Filter20Regular,
  Headset20Regular,
  Mic20Regular,
  MicOff20Regular,
  Open16Regular,
  People20Filled,
  Person20Filled,
  PersonAdd20Regular,
  Pause20Regular,
  Play20Regular,
  Checkmark16Regular,
  Video20Regular,
  VideoOff20Regular,
} from "@fluentui/react-icons";
import type { FluentIcon } from "@fluentui/react-icons";
import type { IContact } from "../../../../types/Contacts";
import { PersonAvatar, fmtDur, fmtHistoryDay, fmtTimer, fullName, useOutsideClose } from "../Shared/Teamsshared";
import styles from "../Internalmessage.module.css";
import { calls as callActions, useCalls } from "../spurt/calls";
import type { CallRecord } from "../spurt/calls";
import { StreamVideo } from "./CallOverlay";
import SpeedDialAdd from "./Speeddialadd";
import { chat } from "../spurt/chatStore";
/* ------------------------------------------------------------------ *
 *  Calls page (Teams style): dial box · call history · speed dial.
 *  Calls run over the Spurt media server (see spurt/calls.ts). The backend
 *  has no personal call log yet, so the history is kept on this device,
 *  per signed-in user.
 * ------------------------------------------------------------------ */

export type CallDirection = "incoming" | "outgoing" | "missed";
export interface CallEntry {
  id: string;
  contactId: string;
  name: string;
  image: string | null;
  online: boolean;
  direction: CallDirection;
  at: number;
  seconds: number;
  read: boolean;
}

const toEntry = (r: CallRecord, byId: Record<string, IContact>): CallEntry => {
  const c = byId[r.contactId];
  return { id: r.id, contactId: r.contactId, name: c ? fullName(c) : r.name, image: c?.profilePicture || null, online: c?.status === true, direction: r.direction, at: r.at, seconds: r.seconds, read: r.read };
};

/** Call history for the signed-in person, shaped for these screens. `setCalls` works like a React state setter. */
export const useCallLog = (contacts: IContact[]) => {
  const history = useCalls((s) => s.history);
  const byId = useMemo(() => {
    const m: Record<string, IContact> = {};
    contacts.forEach((c) => (m[c._id] = c));
    return m;
  }, [contacts]);
  const calls = useMemo(() => history.map((r) => toEntry(r, byId)), [history, byId]);
  const setCalls: React.Dispatch<React.SetStateAction<CallEntry[]>> = useCallback(
    (u) => {
      callActions().setHistory((h) => {
        const next = typeof u === "function" ? u(h.map((r) => toEntry(r, byId))) : u;
        const old = new Map(h.map((r) => [r.id, r]));
        return next.map((e) => ({ ...(old.get(e.id) ?? { name: e.name }), id: e.id, contactId: e.contactId, name: e.name, direction: e.direction, at: e.at, seconds: e.seconds, read: e.read }));
      });
    },
    [byId],
  );
  return { calls, setCalls };
};

type Filter = "all" | "missed" | "incoming" | "outgoing";
const FILTER_LABEL: Record<Filter, string> = { all: "All", missed: "Missed", incoming: "Incoming", outgoing: "Outgoing" };
const DIR_ICON: Record<CallDirection, FluentIcon> = { incoming: CallInbound16Regular, outgoing: CallOutbound16Regular, missed: CallMissed16Regular };
const DIR_LABEL: Record<CallDirection, string> = { incoming: "Incoming", outgoing: "Outgoing", missed: "Missed incoming" };
const FORWARD = ["Don't forward", "Forward to voicemail", "Ring simultaneously on mobile"];
const DEVICES = ["Computer audio", "Headset", "Phone audio"];

export interface TeamsCallsProps {
  contacts: IContact[];
  calls: CallEntry[];
  setCalls: React.Dispatch<React.SetStateAction<CallEntry[]>>;
  onChat: (c: IContact) => void;
  onViewContacts: () => void;
  toast: (msg: string) => void;
  /** Set to start a call from elsewhere (chat header, activity…). `nonce` makes repeats work. */
  dial?: { contact: IContact; nonce: number; video?: boolean } | null | undefined;
  /** change this number to open the "Add to speed dial" dialog from elsewhere */
  speedAdd?: number | undefined;
  /** the signed-in user, so each person keeps their own speed dial in this browser */
  userId?: string | undefined;
}

const TeamsCalls: React.FC<TeamsCallsProps> = ({ contacts, calls, setCalls, onChat, onViewContacts, toast, dial, speedAdd, userId }) => {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<IContact | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [newestFirst, setNewestFirst] = useState(true);
  const [pop, setPop] = useState<string | null>(null);
  const [forward, setForward] = useState(FORWARD[0] || "");
  const [device, setDevice] = useState(DEVICES[0] || "");
  const speedKey = `spurt.speedDial.${userId || "anon"}`;
  const [speed, setSpeedState] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(speedKey);
      const v: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  });
  const setSpeed = (f: (s: string[]) => string[]) =>
    setSpeedState((s) => {
      const next = f(s);
      try {
        window.localStorage.setItem(speedKey, JSON.stringify(next));
      } catch {
        /* storage unavailable: the list lasts until the page closes */
      }
      return next;
    });
  const [speedOpen, setSpeedOpen] = useState(false);
  useEffect(() => {
    if (speedAdd) setSpeedOpen(true);
  }, [speedAdd]);
  const active = useCalls((c) => c.active);
  const phase = useCalls((c) => c.phase);
  const streams = useCalls((c) => c.streams);
  const localStream = useCalls((c) => c.localStream);
  const [now, setNow] = useState(() => Date.now());
  const rootRef = useRef<HTMLDivElement>(null);
  const closePop = useCallback(() => setPop(null), []);
  useOutsideClose(pop, closePop);

  /* ---- call lifecycle (the call itself lives in spurt/calls.ts, so it survives page changes) ---- */
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  const startCall = useCallback(
    (c: IContact, video = false) => {
      setText("");
      setPicked(null);
      setPop(null);
      callActions()
        .start(c._id, video)
        .catch((e: Error) => toast(e.message));
    },
    [toast],
  );

  const endCall = () => {
    const a = active;
    void callActions().end("local");
    if (a) toast(a.connectedAt ? `Call ended · ${fmtDur(Math.max(1, Math.floor((Date.now() - a.connectedAt) / 1000)))}` : "Call cancelled");
  };

  const lastNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!dial || lastNonce.current === dial.nonce) return;
    lastNonce.current = dial.nonce;
    startCall(dial.contact, dial.video === true);
  }, [dial, startCall]);

  /* ---- dial box ---- */
  const q = text.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q || picked) return [];
    return contacts
      .filter((c) => `${fullName(c)} ${c.email} ${c.phoneNumber}`.toLowerCase().indexOf(q) !== -1)
      .slice(0, 6);
  }, [contacts, q, picked]);
  const target = picked || matches[0] || null;

  /* ---- history ---- */
  const shown = useMemo(() => {
    const list = calls.filter((c) => filter === "all" || c.direction === filter);
    list.sort((a, b) => (newestFirst ? b.at - a.at : a.at - b.at));
    return list;
  }, [calls, filter, newestFirst]);
  const contactById = useCallback((id: string) => contacts.filter((c) => c._id === id)[0] || null, [contacts]);
  const speedContacts = speed.map(contactById).filter((c): c is IContact => c !== null);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape" && pop) {
      e.stopPropagation();
      setPop(null);
    }
  };

  const callBack = (c: CallEntry) => {
    const ct = contactById(c.contactId);
    if (ct) startCall(ct);
    else toast("This contact is no longer available");
  };
  const chatWith = (c: CallEntry) => {
    const ct = contactById(c.contactId);
    if (ct) onChat(ct);
  };

  return (
    <div className={styles.callsPage} ref={rootRef} onKeyDown={onKey}>
      {/* page header */}
      <div className={styles.pageHead}>
        <span className={styles.appTile}>
          <Call20Filled />
        </span>
        <h2 className={styles.pageTitle}>Calls</h2>
        <div className={styles.headTabs} role="tablist">
          <button type="button" role="tab" aria-selected="true" className={`${styles.headTab} ${styles.headTabOn}`}>
            Personal
          </button>
        </div>
        <span className={styles.grow} />
        <button type="button" className={styles.outlineBtn} onClick={onViewContacts}>
          View contacts
          <Open16Regular />
        </button>
        <button type="button" className={`${styles.iconBtn} ${styles.iconBtnBrand}`} aria-label="Contacts" title="Contacts" onClick={onViewContacts}>
          <People20Filled />
        </button>
      </div>

      <div className={styles.callsBody}>
        {/* ───────── dial pad ───────── */}
        {(
          <div className={styles.dial}>
            {active ? (
              <div className={styles.inCall} role="group" aria-label="Current call">
                {active.video && streams.some((x) => x.getVideoTracks().length > 0) ? (
                  <div style={{ display: "grid", gap: 8, width: "100%", maxWidth: 480 }}>
                    {streams
                      .filter((x) => x.getVideoTracks().length > 0)
                      .map((x) => (
                        <StreamVideo key={x.id} stream={x} label={`Video of ${active.name}`} />
                      ))}
                    {localStream && active.camOn && (
                      <div style={{ width: 120 }}>
                        <StreamVideo stream={localStream} mirror label="Your camera" />
                      </div>
                    )}
                  </div>
                ) : (
                  <PersonAvatar name={active.name} image={active.contactId ? (contacts.filter((c) => c._id === active.contactId)[0]?.profilePicture ?? null) : null} size={96} />
                )}
                <div className={styles.inCallName}>{active.name}</div>
                <div className={styles.inCallState} aria-live="polite">
                  {active.hold ? "On hold" : active.connectedAt ? fmtTimer(Math.max(0, Math.floor((now - active.connectedAt) / 1000))) : phase === "connecting" ? "Connecting…" : "Calling…"}
                </div>
                <div className={styles.inCallBtns}>
                  <button type="button" className={`${styles.roundBtn} ${active.muted ? styles.roundOn : ""}`} aria-pressed={active.muted} aria-label={active.muted ? "Unmute" : "Mute"} title={active.muted ? "Unmute" : "Mute"} onClick={() => callActions().toggleMute()}>
                    {active.muted ? <MicOff20Regular /> : <Mic20Regular />}
                  </button>
                  {active.video && (
                    <button type="button" className={`${styles.roundBtn} ${!active.camOn ? styles.roundOn : ""}`} aria-pressed={!active.camOn} aria-label={active.camOn ? "Turn camera off" : "Turn camera on"} title={active.camOn ? "Turn camera off" : "Turn camera on"} onClick={() => callActions().toggleCamera()}>
                      {active.camOn ? <Video20Regular /> : <VideoOff20Regular />}
                    </button>
                  )}
                  <button type="button" className={`${styles.roundBtn} ${active.hold ? styles.roundOn : ""}`} aria-pressed={active.hold} aria-label={active.hold ? "Resume" : "Hold"} title={active.hold ? "Resume" : "Hold"} onClick={() => callActions().toggleHold()}>
                    {active.hold ? <Play20Regular /> : <Pause20Regular />}
                  </button>
                  <button type="button" className={`${styles.roundBtn} ${styles.roundEnd}`} aria-label="End call" title="End call" onClick={endCall}>
                    <CallEnd20Filled />
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className={styles.dialInput}>
                  <input
                    value={text}
                    placeholder="Type a name"
                    aria-label="Type a name"
                    onChange={(e) => {
                      setText(e.target.value);
                      setPicked(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && target) startCall(target);
                    }}
                  />
                </div>
                {matches.length > 0 && (
                  <div className={styles.suggest} role="listbox" aria-label="Contacts">
                    {matches.map((c) => (
                      <button
                        key={c._id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        className={styles.suggestRow}
                        onClick={() => {
                          setPicked(c);
                          setText(fullName(c));
                        }}
                      >
                        <PersonAvatar name={fullName(c)} image={c.profilePicture} size={28} online={c.status === true} />
                        <span className={styles.suggestText}>
                          <span>{fullName(c)}</span>
                          <small>{c.email}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <button type="button" className={styles.callBtn} disabled={!target} onClick={() => target && startCall(target)}>
                  <Call20Regular />
                  Call
                </button>
                <button type="button" className={styles.callBtn} disabled={!target} onClick={() => target && startCall(target, true)}>
                  <Video20Regular />
                  Video call
                </button>
              </>
            )}

            <div className={styles.dialFoot}>
              <div className={styles.anchor} data-pop="fwd">
                <button type="button" className={styles.footBtn} aria-haspopup="menu" aria-expanded={pop === "fwd"} onClick={() => setPop(pop === "fwd" ? null : "fwd")}>
                  <CallForward20Regular />
                  {forward}
                  <ChevronDown12Regular />
                </button>
                {pop === "fwd" && (
                  <div className={`${styles.pop} ${styles.popUp}`} role="menu">
                    {FORWARD.map((f) => (
                      <button key={f} type="button" role="menuitemradio" aria-checked={forward === f} className={styles.popItem} onClick={() => { setForward(f); setPop(null); }}>
                        <span className={styles.tick}>{forward === f && <Checkmark16Regular />}</span>
                        {f}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className={styles.anchor} data-pop="dev">
                <button type="button" className={styles.footBtn} aria-haspopup="menu" aria-expanded={pop === "dev"} onClick={() => setPop(pop === "dev" ? null : "dev")}>
                  <Headset20Regular />
                  {device}
                  <ChevronDown12Regular />
                </button>
                {pop === "dev" && (
                  <div className={`${styles.pop} ${styles.popUp}`} role="menu">
                    {DEVICES.map((f) => (
                      <button key={f} type="button" role="menuitemradio" aria-checked={device === f} className={styles.popItem} onClick={() => { setDevice(f); setPop(null); }}>
                        <span className={styles.tick}>{device === f && <Checkmark16Regular />}</span>
                        {f}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ───────── history ───────── */}
        <div className={styles.history}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionTitle}>
              <ChevronDown12Regular />
              History
            </span>
            <span className={styles.grow} />
            <div className={styles.anchor} data-pop="filter">
              <button type="button" className={styles.pillBrand} aria-haspopup="menu" aria-expanded={pop === "filter"} onClick={() => setPop(pop === "filter" ? null : "filter")}>
                {FILTER_LABEL[filter]}
                <ChevronDown12Regular />
              </button>
              {pop === "filter" && (
                <div className={`${styles.pop} ${styles.popRight}`} role="menu">
                  {(Object.keys(FILTER_LABEL) as Filter[]).map((f) => (
                    <button key={f} type="button" role="menuitemradio" aria-checked={filter === f} className={styles.popItem} onClick={() => { setFilter(f); setPop(null); }}>
                      <span className={styles.tick}>{filter === f && <Checkmark16Regular />}</span>
                      {FILTER_LABEL[f]}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className={styles.iconBtn} aria-label={newestFirst ? "Showing newest first" : "Showing oldest first"} title={newestFirst ? "Newest first" : "Oldest first"} onClick={() => setNewestFirst(!newestFirst)}>
              <Filter20Regular />
            </button>
          </div>

          <div className={styles.historyList} role="list" aria-label="Call history">
            {shown.length === 0 && <div className={styles.emptyNote}>{calls.length === 0 ? "No calls yet" : "No calls match this filter"}</div>}
            {shown.map((c) => {
              const Dir = DIR_ICON[c.direction];
              return (
                <div key={c.id} role="listitem" className={styles.callRow}>
                  <PersonAvatar name={c.name} image={c.image} size={32} online={c.online} />
                  <div className={styles.callMain}>
                    <div className={`${styles.callName} ${!c.read ? styles.strong : ""}`}>{c.name}</div>
                    <div className={`${styles.callSub} ${c.direction === "missed" ? styles.missed : ""}`}>
                      <Dir />
                      {DIR_LABEL[c.direction]}
                    </div>
                  </div>
                  <div className={styles.callActions}>
                    <button type="button" className={styles.iconBtn} aria-label={`Call ${c.name}`} title="Call" onClick={() => callBack(c)}>
                      <Call20Regular />
                    </button>
                    <button type="button" className={styles.iconBtn} aria-label={`Chat with ${c.name}`} title="Chat" onClick={() => chatWith(c)}>
                      <Chat20Regular />
                    </button>
                    <button type="button" className={styles.iconBtn} aria-label="Remove from history" title="Remove" onClick={() => setCalls((cs) => cs.filter((x) => x.id !== c.id))}>
                      <Delete20Regular />
                    </button>
                  </div>
                  <div className={styles.callWhen}>
                    <span>{fmtHistoryDay(c.at, now)}</span>
                    <span>{fmtDur(c.seconds)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ───────── speed dial ───────── */}
        <div className={styles.speed}>
          <div className={styles.sectionHead}>
            <span className={styles.sectionTitleLg}>Speed dial</span>
            <span className={styles.grow} />
            <button type="button" className={styles.iconBtn} aria-label="Add to speed dial" title="Add to speed dial" aria-haspopup="dialog" onClick={() => { setSpeedOpen(true); setPop(null); }}>
              <PersonAdd20Regular />
            </button>
          </div>
          {speedContacts.length === 0 ? (
            <div className={styles.speedEmpty}>
              <div className={styles.illus} aria-hidden="true">
                <span className={`${styles.orb} ${styles.orb1}`}>
                  <Person20Filled />
                </span>
                <span className={`${styles.orb} ${styles.orb2}`}>
                  <Person20Filled />
                </span>
                <span className={`${styles.orb} ${styles.orb3}`}>
                  <Add16Regular />
                </span>
              </div>
              <p className={styles.speedText}>Add people to speed dial for quick access.</p>
              <button type="button" className={styles.outlineBtn} onClick={() => setSpeedOpen(true)}>
                <PersonAdd20Regular />
                Add
              </button>
            </div>
          ) : (
            <div className={styles.speedList}>
              {speedContacts.map((c) => (
                <div key={c._id} className={styles.callRow}>
                  <PersonAvatar name={fullName(c)} image={c.profilePicture} size={32} online={c.status === true} />
                  <div className={styles.callMain}>
                    <div className={styles.callName}>{fullName(c)}</div>
                    <div className={styles.callSub}>{c.phoneNumber}</div>
                  </div>
                  <div className={`${styles.callActions} ${styles.callActionsOn}`}>
                    <button type="button" className={styles.iconBtn} aria-label={`Call ${fullName(c)}`} title="Call" onClick={() => startCall(c)}>
                      <Call20Regular />
                    </button>
                    <button type="button" className={styles.iconBtn} aria-label={`Remove ${fullName(c)} from speed dial`} title="Remove" onClick={() => setSpeed((s) => s.filter((x) => x !== c._id))}>
                      <Delete20Regular />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {speedOpen && (
        <SpeedDialAdd
          contacts={contacts.filter((c) => speed.indexOf(c._id) === -1)}
          onQuery={(q) => chat.loadDirectory(q)}
          onClose={() => setSpeedOpen(false)}
          onAdd={(c) => { setSpeed((s) => (s.indexOf(c._id) === -1 ? s.concat([c._id]) : s)); setSpeedOpen(false); toast(`${fullName(c)} added to speed dial`); }}
        />
      )}
    </div>
  );
};

export default TeamsCalls;